import { useEffect, useState } from 'react'
import {
  fetchProviderModels,
  getAvailableProviders,
  type ModelInfo,
  type ProviderType,
} from '@/lib/ai-providers'
import {
  getSelectedModel,
  getSelectedProvider,
  saveSelectedModel,
  saveSelectedProvider,
} from '@/types/UserSettings'

interface AIProviderSelectorProps {
  onProviderChange: (provider: ProviderType) => void
  onModelChange: (modelId: string) => void
  onProviderAndModelReady: (provider: ProviderType, model: string) => void
}

interface AIProviderSelectorState {
  selectedProvider: ProviderType | null
  selectedModel: string
  availableModels: ModelInfo[]
  isLoadingModels: boolean
  modelError: string | null
  handleProviderChange: (provider: ProviderType) => Promise<void>
  handleModelChange: (modelId: string) => void
}

export function useAIProviderSelector({
  onProviderChange,
  onModelChange,
  onProviderAndModelReady,
}: AIProviderSelectorProps): AIProviderSelectorState {
  const [selectedProvider, setSelectedProvider] = useState<ProviderType | null>(
    null
  )
  const [selectedModel, setSelectedModel] = useState<string>('')
  const [availableModels, setAvailableModels] = useState<ModelInfo[]>([])
  const [isLoadingModels, setIsLoadingModels] = useState(false)
  const [modelError, setModelError] = useState<string | null>(null)

  const availableProviders = getAvailableProviders()

  const loadModels = async (provider: ProviderType) => {
    const savedModel = getSelectedModel(provider)

    if (savedModel) {
      setSelectedModel(savedModel)
      setAvailableModels([{ id: savedModel, description: 'Loading...' }])
      setModelError(null)
    } else {
      setIsLoadingModels(true)
      setModelError(null)
    }

    try {
      const models = await fetchProviderModels(provider)
      setAvailableModels(models)

      if (savedModel && models.some((model) => model.id === savedModel)) {
        setSelectedModel(savedModel)
        onProviderAndModelReady(provider, savedModel)
      } else if (models.length > 0) {
        setSelectedModel(models[0].id)
        onProviderAndModelReady(provider, models[0].id)
      }
    } catch (error) {
      setModelError(
        error instanceof Error ? error.message : 'Failed to load models'
      )
      setAvailableModels([])
    } finally {
      setIsLoadingModels(false)
    }
  }

  const handleProviderChange = async (provider: ProviderType) => {
    setSelectedProvider(provider)
    saveSelectedProvider(provider)
    onProviderChange(provider)

    const savedModel = getSelectedModel(provider)
    if (savedModel) {
      setSelectedModel(savedModel)
      setAvailableModels([{ id: savedModel, description: 'Loading...' }])
      setIsLoadingModels(false)
      setModelError(null)
    } else {
      setSelectedModel('')
      setAvailableModels([])
      setIsLoadingModels(true)
      setModelError(null)
    }

    await loadModels(provider)
  }

  const handleModelChange = (modelId: string) => {
    setSelectedModel(modelId)
    if (selectedProvider) {
      saveSelectedModel(selectedProvider, modelId)
    }
    onModelChange(modelId)
  }

  // Initialize AI provider
  useEffect(() => {
    if (availableProviders.length > 0 && !selectedProvider) {
      const savedProvider = getSelectedProvider()
      const providerToUse =
        savedProvider &&
        availableProviders.includes(savedProvider as ProviderType)
          ? (savedProvider as ProviderType)
          : availableProviders[0]

      setSelectedProvider(providerToUse)

      const savedModel = getSelectedModel(providerToUse)
      if (savedModel) {
        setSelectedModel(savedModel)
        setAvailableModels([{ id: savedModel, description: 'Loading...' }])
        setIsLoadingModels(false)
        setModelError(null)
      } else {
        setIsLoadingModels(true)
        setModelError(null)
      }

      loadModels(providerToUse)
    }
  }, [availableProviders, selectedProvider, loadModels])

  return {
    selectedProvider,
    selectedModel,
    availableModels,
    isLoadingModels,
    modelError,
    handleProviderChange,
    handleModelChange,
  }
}

export const AIProviderSelector = {
  useAIProviderSelector,
}
